import axios from "axios";
import { USER_DIRECTORY_PAGE_SIZE } from "../constants/directoryConstants";
import apiEndpointManager from "../config"; // Corrected import path

// Interface for user profile card data returned from API
export interface UserProfileCardData {
    id: string;
    name: string;
    location?: string;
    profileImageUrl?: string;
    bannerImageUrl?: string;
    aboutSnippet?: string;
    title?: string;
    linkedinURL?: string;
}

// Interface for API response
export interface UserDirectoryResponse {
    users: UserProfileCardData[];
    hasNextPage: boolean;
    lastEvaluatedKey: string | null;
}

// Sample mock data to show when API fails (for development)
const mockUsers: UserProfileCardData[] = [
    {
        id: "user1",
        name: "Ahmed Khan",
        location: "Toronto, ON",
        profileImageUrl: "https://randomuser.me/api/portraits/men/1.jpg",
        bannerImageUrl: "https://source.unsplash.com/random/800x200/?gradient,blue",
        aboutSnippet: "Software Engineer with 5+ years of experience in full-stack development",
    },
    {
        id: "user2",
        name: "Fatima Zahra",
        location: "Montreal, QC",
        profileImageUrl: "https://randomuser.me/api/portraits/women/2.jpg",
        bannerImageUrl: "https://source.unsplash.com/random/800x200/?gradient,purple",
        aboutSnippet: "Marketing professional specializing in digital campaigns for non-profits",
    },
    {
        id: "user3",
        name: "Amir Rahman",
        location: "Vancouver, BC",
        profileImageUrl: "https://randomuser.me/api/portraits/men/3.jpg",
        bannerImageUrl: "https://source.unsplash.com/random/800x200/?gradient,green",
        aboutSnippet: "Medical researcher focused on immunology and infectious diseases",
    },
    {
        id: "user4",
        name: "Aisha Malik",
        location: "Calgary, AB",
        profileImageUrl: "https://randomuser.me/api/portraits/women/4.jpg",
        bannerImageUrl: "https://source.unsplash.com/random/800x200/?gradient,red",
        aboutSnippet: "Financial advisor helping families build sustainable wealth",
    },
    {
        id: "user5",
        name: "Omar Yusuf",
        location: "Ottawa, ON",
        profileImageUrl: "https://randomuser.me/api/portraits/men/5.jpg",
        bannerImageUrl: "https://source.unsplash.com/random/800x200/?gradient,yellow",
        aboutSnippet:
            "Startup founder building education technology solutions for underserved communities",
    },
    {
        id: "user6",
        name: "Nadia Hussain",
        location: "Edmonton, AB",
        profileImageUrl: "https://randomuser.me/api/portraits/women/6.jpg",
        bannerImageUrl: "https://source.unsplash.com/random/800x200/?gradient,pink",
        aboutSnippet:
            "Environmental scientist studying climate change impacts on Canadian ecosystems",
    },
];

// Generate mock data for a specific letter
const generateMockDataForLetter = (letter: string): UserProfileCardData[] => {
    return mockUsers.map((user, index) => ({
        ...user,
        id: `${letter.toLowerCase()}-user${index}`,
        name: `${letter}${user.name.substring(1)}`,
    }));
};

// Feature flag for using mock data (set to false to use real API)
const USE_MOCK_DATA = false;

// Directory API service
const directoryService = {
    /**
     * Fetch directory users starting with a specific letter
     * @param letter - The letter to search for (A-Z)
     * @param limit - Optional limit for number of results
     * @param exclusiveStartKey - Optional pagination token from previous request
     * @returns Promise with user directory data
     */
    fetchDirectoryUsers: async (
        letter: string,
        limit: number = USER_DIRECTORY_PAGE_SIZE,
        exclusiveStartKey?: string,
        searchTerm?: string,
    ): Promise<UserDirectoryResponse> => {
        console.log(
            ` Directory Service: Fetching users for letter "${letter}" and search "${searchTerm}"`,
        );

        // For development, use mock data
        if (USE_MOCK_DATA) {
            const mockData = generateMockDataForLetter(letter);
            console.log(
                `🧪 Using MOCK data for letter "${letter}" - Generated ${mockData.length} users`,
            );
            return {
                users: mockData,
                hasNextPage: false,
                lastEvaluatedKey: null,
            };
        }

        try {
            // Build query parameters
            const params = new URLSearchParams({
                limit: limit.toString(),
            });

            // Conditionally add letter or search term
            if (searchTerm) {
                params.append("search", searchTerm);
            } else if (letter) {
                params.append("startsWith", letter);
            }

            // Add pagination token if provided
            if (exclusiveStartKey) {
                params.append("exclusiveStartKey", exclusiveStartKey);
            }

            const baseEndpoint = await apiEndpointManager.getEndpoint();
            const apiUrl = `${baseEndpoint}/directory/users?${params.toString()}`;

            console.log(`🔍 API Request with params:`, params.toString());
            console.log(`🔩 Constructed API URL: ${apiUrl}`);

            // Make the API request
            const response = await axios.get<UserDirectoryResponse>(apiUrl, {
                headers: {
                    "Content-Type": "application/json",
                },
                // Add a timeout to prevent hanging requests
                timeout: 15000,
            });

            // For debugging purposes, log the response
            console.log(
                `✅ Received ${
                    response.data?.users?.length || 0
                } users for letter/search: ${letter}/${searchTerm}`,
            );

            // Return empty array if no users property in response
            if (!response.data || !response.data.users) {
                console.warn("⚠️ API response missing users array, returning empty data");
                return {
                    users: [],
                    hasNextPage: false,
                    lastEvaluatedKey: null,
                };
            }

            return response.data;
        } catch (error) {
            console.error("❌ Error fetching directory users:", error);

            // Return empty data on error
            return {
                users: [],
                hasNextPage: false,
                lastEvaluatedKey: null,
            };
        }
    },
};

export default directoryService;
